import {
  CriterionResult,
  DiagnosticReport,
  OverallVerdict,
  PhaseMessage,
  ProbeMessage,
  ReadyMessage,
  SegmentRun,
  TestMode,
} from "./sse-test.types";

/**
 * Pure logic for the SSE troubleshooting tool (IDS-7243): segment orchestration metadata, verdict computation and
 * report/export builders. Kept free of React and side effects so it can be unit-tested in isolation.
 *
 * All comparisons use the client receive time relative to the segment start (`recvMs`) versus the server-provided
 * `elapsedMs`, so any client/server clock offset cancels out.
 */

/** Static connection segments per mode, in order. Ramp is dynamic (a raw first-byte probe + one stream connection per idle gap) and handled by the component. */
export const SEGMENTS_FOR_MODE: Record<TestMode, ("first-byte" | "stream")[]> = {
  quick: ["stream"],
  threshold: ["stream"],
  ramp: [],
};

/** Rough pre-start duration hint (ms). Authoritative value comes from the server `plan` event once the run starts. */
export const LOCAL_ESTIMATE_MS: Record<TestMode, number> = {
  quick: 45_000,
  threshold: 260_000,
  ramp: 900_000,
};

export function formatDuration(ms: number | undefined): string {
  if (ms === undefined || ms < 0) return "-";
  if (ms < 1000) return `${ms} ms`;
  const seconds = Math.round(ms / 100) / 10;
  if (seconds < 90) return `${seconds}s`;
  return `${Math.round(seconds / 6) / 10} min`;
}

/** Formats a duration as a M:SS clock, e.g. 83000 -> "1:23". Used for the live progress timer. */
export function formatClock(ms: number | undefined): string {
  if (ms === undefined || ms < 0) return "0:00";
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

function lastRecvMs(run: SegmentRun): number {
  return run.events.reduce((max, e) => Math.max(max, e.recvMs), 0);
}

function connectionCriterion(segments: SegmentRun[]): CriterionResult {
  const opened = segments.find((s) => s.hadOpen);
  if (opened) {
    return {
      id: "connection",
      label: "Connection established (onopen)",
      state: "PASS",
      measuredMs: opened.onOpenMs,
      detail: `Response headers received (onopen) after ${formatDuration(opened.onOpenMs)}.`,
    };
  }
  return {
    id: "connection",
    label: "Connection established (onopen)",
    state: "FAIL",
    detail: "onopen never fired: the stream response headers did not arrive.",
  };
}

function firstByteCriterion(segments: SegmentRun[]): CriterionResult {
  const label = "First byte (first body byte)";
  const seg = segments.find((s) => s.segment === "first-byte");
  if (!seg) {
    return {
      id: "first-byte",
      label,
      state: "NOT_TESTED",
      detail:
        "Not applicable in this mode: like the real elaboration channel, the connection sends its first byte immediately. Raw first-byte tolerance is probed only in the progressive (ramp) mode.",
    };
  }
  const ready = seg.events.find((e) => e.message.type === "ready");
  if (seg.gotDone && ready) {
    const elapsed = (ready.message as ReadyMessage).elapsedMs;
    return {
      id: "first-byte",
      label,
      state: "PASS",
      measuredMs: elapsed,
      detail: `First body byte arrived after a ${formatDuration(elapsed)} server-side delay; no device aborted it.`,
    };
  }
  const survived = seg.errorMs ?? lastRecvMs(seg);
  return {
    id: "first-byte",
    label,
    state: "FAIL",
    measuredMs: survived,
    detail:
      `Connection dropped after ~${formatDuration(survived)} before the first body byte — consistent with a ` +
      `first-byte/read timeout below that value on a device between the client and our backend.`,
  };
}

function idleCriterion(segments: SegmentRun[]): CriterionResult {
  const label = "Idle / read timeout";
  const streams = segments.filter((s) => s.segment === "stream");
  if (streams.length === 0) {
    return { id: "idle", label, state: "NOT_TESTED", detail: "Not tested in this mode." };
  }

  // Each stream connection tests exactly one idle gap; ramp mode runs several. Aggregate across all of them.
  const gapResults = streams
    .map((s) => {
      const idleStart = s.events.find(
        (e) =>
          e.message.type === "phase" &&
          (e.message as PhaseMessage).name === "idle" &&
          (e.message as PhaseMessage).state === "start"
      );
      if (!idleStart) return null;
      const gap = (idleStart.message as PhaseMessage).expectedGapMs ?? 0;
      const survivedMs = s.gotDone ? gap : Math.max(0, (s.errorMs ?? lastRecvMs(s)) - idleStart.recvMs);
      return { gap, survived: s.gotDone, survivedMs };
    })
    .filter((r): r is { gap: number; survived: boolean; survivedMs: number } => r !== null);

  if (gapResults.length === 0) {
    return {
      id: "idle",
      label,
      state: "INCONCLUSIVE",
      detail: "Connection dropped before the idle phase; cannot judge.",
    };
  }

  const maxSurvivedGap = gapResults.filter((r) => r.survived).reduce((m, r) => Math.max(m, r.gap), 0);
  const firstFailed = gapResults.filter((r) => !r.survived).sort((a, b) => a.gap - b.gap)[0];

  if (!firstFailed) {
    return {
      id: "idle",
      label,
      state: "PASS",
      measuredMs: maxSurvivedGap,
      thresholdMs: maxSurvivedGap,
      detail: `Survived silent gaps up to ${formatDuration(maxSurvivedGap)} with the connection intact (our channel renews at ~4 min).`,
    };
  }
  return {
    id: "idle",
    label,
    state: "FAIL",
    measuredMs: firstFailed.survivedMs,
    thresholdMs: firstFailed.gap,
    detail:
      `Connection dropped after ~${formatDuration(firstFailed.survivedMs)} of silence` +
      (maxSurvivedGap > 0 ? ` (survived up to ${formatDuration(maxSurvivedGap)})` : "") +
      ` — consistent with an idle/read timeout at ~${formatDuration(firstFailed.survivedMs)} on a device between the client ` +
      `and our backend. Our real channel renews at ~4 min, so it may still work despite this.`,
  };
}

function bufferingCriterion(segments: SegmentRun[]): CriterionResult {
  const label = "Response buffering";
  const seg = segments.find((s) => s.segment === "stream");
  const tolerances = seg?.plan?.tolerances;
  const burst = (seg?.events ?? []).filter(
    (e) => e.message.type === "probe" && (e.message as ProbeMessage).phase === "burst"
  );
  if (!tolerances || burst.length < 2) {
    return {
      id: "buffering",
      label,
      state: "INCONCLUSIVE",
      detail: "Too few burst events received to judge buffering.",
    };
  }

  let maxCluster = 1;
  for (let i = 0; i < burst.length; i++) {
    let count = 1;
    for (let j = i + 1; j < burst.length; j++) {
      if (burst[j].recvMs - burst[i].recvMs <= tolerances.bufferingClusterWindowMs) {
        count++;
      } else {
        break;
      }
    }
    maxCluster = Math.max(maxCluster, count);
  }

  let maxSkew = 0;
  for (let i = 1; i < burst.length; i++) {
    const clientDelta = burst[i].recvMs - burst[i - 1].recvMs;
    const serverDelta = (burst[i].message as ProbeMessage).elapsedMs - (burst[i - 1].message as ProbeMessage).elapsedMs;
    maxSkew = Math.max(maxSkew, Math.abs(clientDelta - serverDelta));
  }

  const clustered = maxCluster > tolerances.bufferingMaxClusterSize;
  const skewed = maxSkew > tolerances.bufferingMaxSkewMs;
  if (clustered || skewed) {
    return {
      id: "buffering",
      label,
      state: "FAIL",
      measuredMs: maxSkew,
      detail:
        `Burst events arrived clustered (max ${maxCluster} within ${tolerances.bufferingClusterWindowMs} ms, ` +
        `max skew ${maxSkew} ms) instead of at the server cadence — response buffering is likely active.`,
    };
  }
  return {
    id: "buffering",
    label,
    state: "PASS",
    measuredMs: maxSkew,
    detail: `Burst events tracked the server cadence (max skew ${maxSkew} ms); no buffering detected.`,
  };
}

function computeOverall(criteria: CriterionResult[], segments: SegmentRun[]): OverallVerdict {
  if (!segments.some((s) => s.hadOpen)) {
    return "CONNECTION_ERROR";
  }
  if (criteria.some((c) => c.state === "FAIL")) {
    return "FAIL";
  }
  const tested = criteria.filter((c) => c.state === "PASS" || c.state === "FAIL");
  if (tested.length > 0 && tested.every((c) => c.state === "PASS")) {
    return "PASS";
  }
  return "INCONCLUSIVE";
}

export function computeReport(
  mode: TestMode,
  testId: string,
  startedAtIso: string,
  segments: SegmentRun[]
): DiagnosticReport {
  const criteria = [
    connectionCriterion(segments),
    firstByteCriterion(segments),
    idleCriterion(segments),
    bufferingCriterion(segments),
  ];
  const overall = computeOverall(criteria, segments);
  const connectionError =
    overall === "CONNECTION_ERROR"
      ? "No connection was established (onopen never fired): likely an authentication or connectivity error, not a network timeout."
      : undefined;
  return { testId, mode, startedAtIso, overall, criteria, connectionError, segments };
}

export interface ReportMeta {
  backendUrl?: string;
  tenantId?: string;
  userName?: string;
  clientId?: string;
}

export function buildReportMarkdown(report: DiagnosticReport, meta: ReportMeta): string {
  const lines: string[] = [];
  lines.push("# SSE channel diagnostic report");
  lines.push("");
  lines.push(`- Overall verdict: **${report.overall}**`);
  lines.push(`- Test id: ${report.testId}`);
  lines.push(`- Mode: ${report.mode}`);
  lines.push(`- Started at: ${report.startedAtIso}`);
  if (meta.backendUrl) lines.push(`- Backend: ${meta.backendUrl}`);
  if (meta.tenantId) lines.push(`- Tenant: ${meta.tenantId}`);
  if (meta.userName) lines.push(`- User: ${meta.userName}`);
  if (meta.clientId) lines.push(`- Client: ${meta.clientId}`);
  if (report.connectionError) {
    lines.push("");
    lines.push(`> ${report.connectionError}`);
  }
  lines.push("");
  lines.push("## Criteria");
  report.criteria.forEach((c) => {
    const measured = c.measuredMs !== undefined ? ` (measured ${formatDuration(c.measuredMs)}` : "";
    const threshold = c.thresholdMs !== undefined ? `, target ${formatDuration(c.thresholdMs)})` : measured ? ")" : "";
    lines.push(`- **${c.label}**: ${c.state}${measured}${threshold}`);
    lines.push(`  - ${c.detail}`);
  });
  lines.push("");
  lines.push(
    "_Which specific network device enforces a failing limit is out of scope for this tool; the numbers above are objective evidence for the customer's IT._"
  );
  return lines.join("\n");
}

export function buildTimelineJson(report: DiagnosticReport): string {
  return JSON.stringify(
    {
      testId: report.testId,
      mode: report.mode,
      startedAtIso: report.startedAtIso,
      overall: report.overall,
      segments: report.segments.map((s) => ({
        segment: s.segment,
        hadOpen: s.hadOpen,
        onOpenMs: s.onOpenMs,
        gotDone: s.gotDone,
        errored: s.errored,
        errorMs: s.errorMs,
        events: s.events.map((e) => ({ recvMs: e.recvMs, ...e.message })),
      })),
    },
    null,
    2
  );
}
